import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import type { ComponentPropsWithoutRef, MutableRefObject } from "react";
import { format } from "date-fns";
import { Button, Checkbox, SegmentedTabs, Switch, Input } from "@akuna/ui";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { DateFieldPopover } from "@/components/ui/date-field-popover";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import type { MedicalRecordInput } from "@/lib/validations/medical-record";
import {
  hasMedicalRecordSectionData,
  hasValue,
} from "@/components/medical-records/form/medical-record-form-sections";
import type {
  MedicalRecordFormController,
  MedicalRecordFormHandle,
} from "@/components/medical-records/form/use-medical-record-form";

export type { MedicalRecordFormHandle } from "@/components/medical-records/form/use-medical-record-form";

interface MedicalRecordFormProps {
  formController: MedicalRecordFormController;
  onCancel: () => void;
  isLoading?: boolean;
  readOnly?: boolean;
  variant?: "desktop" | "mobile";
}

const formatDateTimeForReadOnly = (value?: Date | string | null) => {
  if (!value) return null;
  const dateValue = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(dateValue.getTime())) return null;
  return format(dateValue, "dd/MM/yyyy 'às' HH:mm");
};

const formatDateForReadOnly = (value?: Date | string | null) => {
  if (!value) return null;
  const dateValue = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(dateValue.getTime())) return null;
  return format(dateValue, "dd/MM/yyyy");
};

const trimTextValue = (value?: string | null) => {
  if (typeof value !== "string") return null;
  return value.trim().length > 0 ? value : null;
};

const formatNumberValue = (value?: number | null) => {
  if (value === null || value === undefined) return null;
  if (Number.isNaN(value)) return null;
  return String(value);
};

const appendSuffix = (value: string | null, suffix?: string) => {
  if (!value) return null;
  return suffix ? `${value} ${suffix}` : value;
};

const hasAnamneseData = (values: MedicalRecordInput) =>
  hasMedicalRecordSectionData("anamnesis", values);
const hasExameFisicoData = (values: MedicalRecordInput) =>
  hasMedicalRecordSectionData("physical-exam", values);
const hasDiagnosticoData = (values: MedicalRecordInput) =>
  hasMedicalRecordSectionData("diagnosis", values);
const hasTratamentoData = (values: MedicalRecordInput) =>
  hasMedicalRecordSectionData("treatment", values);

const ReadOnlyField = ({
  value,
  fallback,
  alwaysShow = false,
}: {
  value?: string | null;
  fallback?: string;
  alwaysShow?: boolean;
}) => {
  // Se alwaysShow for true (ex: data da consulta), sempre mostrar com fallback
  if (alwaysShow) {
    return (
      <div className="md:w-fit rounded-md border border-border/50 bg-muted/30 px-3 py-2 text-sm text-foreground">
        <p className="whitespace-pre-wrap leading-relaxed">
          {value ?? <span className="text-muted-foreground">{fallback}</span>}
        </p>
      </div>
    );
  }

  // Caso contrário, não renderizar se não houver valor
  if (!hasValue(value)) return null;

  return (
    <div className="rounded-md border border-border/50 bg-white px-3 py-2 text-sm text-foreground">
      <p className="whitespace-pre-wrap leading-relaxed">{value}</p>
    </div>
  );
};

const TEXTAREA_MIN_HEIGHT_PX = 80;
const TEXTAREA_MAX_HEIGHT_PX = 320;

type BaseTextareaProps = ComponentPropsWithoutRef<typeof Textarea>;

const AutoGrowTextarea = forwardRef<HTMLTextAreaElement, BaseTextareaProps>(
  ({ className, onInput, value, ...props }, forwardedRef) => {
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);

    const resizeToFitContent = useCallback(() => {
      const element = textareaRef.current;
      if (!element) return;

      element.style.height = "auto";
      const scrollHeight = element.scrollHeight;
      const nextHeight = Math.max(
        TEXTAREA_MIN_HEIGHT_PX,
        Math.min(scrollHeight, TEXTAREA_MAX_HEIGHT_PX),
      );

      element.style.height = `${nextHeight}px`;
      element.style.overflowY =
        scrollHeight > TEXTAREA_MAX_HEIGHT_PX ? "auto" : "hidden";
    }, []);

    const setRefs = useCallback(
      (node: HTMLTextAreaElement | null) => {
        textareaRef.current = node;

        if (!forwardedRef) return;
        if (typeof forwardedRef === "function") {
          forwardedRef(node);
          return;
        }
        (forwardedRef as MutableRefObject<HTMLTextAreaElement | null>).current =
          node;
      },
      [forwardedRef],
    );

    useEffect(() => {
      resizeToFitContent();
    }, [resizeToFitContent, value]);

    return (
      <Textarea
        {...props}
        value={value}
        ref={setRefs}
        className={cn(
          "min-h-[80px] max-h-[320px] resize-y overflow-hidden",
          className,
        )}
        onInput={(event) => {
          resizeToFitContent();
          onInput?.(event);
        }}
      />
    );
  },
);

AutoGrowTextarea.displayName = "AutoGrowTextarea";

export const MedicalRecordForm = forwardRef<MedicalRecordFormHandle, MedicalRecordFormProps>(
  (
    {
      formController,
      onCancel,
      isLoading,
      readOnly,
      variant = "desktop",
    },
    ref,
  ) => {
    const { t } = useTranslation("medicalRecords");
    const { form, handleSubmit, actions } = formController;
    const isReadOnly = Boolean(readOnly);
    const followUpDate = form.watch("follow_up_date");
    const createFollowUpAppointment = form.watch("create_follow_up_appointment");
    const { errors } = form.formState;

    useImperativeHandle(ref, () => actions, [actions]);

    return (
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(handleSubmit)}
          className={cn(
            "min-w-0 overflow-hidden",
            variant === "mobile" ? "space-y-4" : "space-y-6",
          )}
        >
          {/* Data da Consulta */}
          <FormField
            control={form.control}
            name="consultation_date"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>{t("form.consultationDate")}</FormLabel>
                <FormControl>
                  {isReadOnly ? (
                    <ReadOnlyField
                      value={formatDateTimeForReadOnly(field.value)}
                      fallback={t("form.consultationWithoutDate")}
                      alwaysShow
                    />
                  ) : (
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "md:w-fit pl-3 text-left font-normal",
                            !field.value && "text-muted-foreground"
                          )}
                        >
                          {field.value ? (
                            format(field.value instanceof Date ? field.value : new Date(field.value), "dd/MM/yyyy 'às' HH:mm")
                          ) : (
                            <span>{t("form.selectDateTime")}</span>
                          )}
                          <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-full p-0" align="start">
                        <div className="flex flex-col gap-4 p-3 sm:flex-row sm:items-start">
                          <Calendar
                            mode="single"
                            selected={field.value ? new Date(field.value) : undefined}
                            onSelect={(date) => {
                              if (!date) {
                                field.onChange(undefined);
                                return;
                              }

                              const currentValue = field.value ? new Date(field.value) : new Date();
                              const updated = new Date(
                                date.getFullYear(),
                                date.getMonth(),
                                date.getDate(),
                                currentValue.getHours(),
                                currentValue.getMinutes(),
                                0,
                                0,
                              );

                              field.onChange(updated);
                            }}
                            disabled={(date) => {
                              const today = new Date();
                              today.setHours(23, 59, 59, 999);
                              return date > today || date < new Date("1900-01-01");
                            }}
                            initialFocus
                          />
                          <div className="flex min-w-[160px] flex-col gap-2">
                            <p className="text-sm font-medium text-foreground">{t("form.time")}</p>
                            <Input
                              type="time"
                              value={
                                field.value
                                  ? format(field.value instanceof Date ? field.value : new Date(field.value), "HH:mm")
                                  : ""
                              }
                              onChange={(event) => {
                                const timeValue = event.target.value;
                                if (!timeValue) {
                                  return;
                                }

                                const [hours, minutes] = timeValue.split(":").map(Number);
                                const base = field.value ? new Date(field.value) : new Date();
                                const updated = new Date(base);
                                updated.setHours(hours, minutes, 0, 0);
                                field.onChange(updated);
                              }}
                            />
                            <Button
                              variant="outline"
                              type="button"
                              onClick={() => {
                                const base = field.value ? new Date(field.value) : new Date();
                                field.onChange(base);
                              }}
                            >
                              {t("form.useCurrentTime")}
                            </Button>
                          </div>
                        </div>
                      </PopoverContent>
                    </Popover>
                  )}
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <SegmentedTabs
            className="[&_.akuna-seg-tabs\_\_panel]:space-y-2"
            defaultValue="anamnese"
            fill="screen"
            variant="outline"
            items={[
              {
                value: "anamnese",
                label: t("form.tabs.anamnesis"),
                content: (
                  <>
              {isReadOnly && !hasAnamneseData(form.getValues()) ? (
                <div className="flex items-center justify-center py-12 text-center">
                  <p className="text-sm text-muted-foreground">
                    {t("form.emptySection")}
                  </p>
                </div>
              ) : (
                <>
                  <FormField
                    control={form.control}
                    name="chief_complaint"
                    render={({ field }) => {
                      const fieldValue = trimTextValue(field.value);
                      if (isReadOnly && !hasValue(fieldValue)) return null;
                      return (
                        <FormItem>
                          <FormLabel>{t("form.chiefComplaint")}</FormLabel>
                          <FormControl>
                            {isReadOnly ? (
                              <ReadOnlyField value={fieldValue} />
                            ) : (
                              <AutoGrowTextarea
                                placeholder={t("form.chiefComplaintPlaceholder")}
                                {...field}
                              />
                            )}
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      );
                    }}
                  />

                  <FormField
                    control={form.control}
                    name="history"
                    render={({ field }) => {
                      const fieldValue = trimTextValue(field.value);
                      if (isReadOnly && !hasValue(fieldValue)) return null;
                      return (
                        <FormItem>
                          <FormLabel>{t("form.history")}</FormLabel>
                          <FormControl>
                            {isReadOnly ? (
                              <ReadOnlyField value={fieldValue} />
                            ) : (
                              <AutoGrowTextarea
                                placeholder={t("form.historyPlaceholder")}
                                {...field}
                              />
                            )}
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      );
                    }}
                  />

                  <FormField
                    control={form.control}
                    name="behavior"
                    render={({ field }) => {
                      const fieldValue = trimTextValue(field.value);
                      if (isReadOnly && !hasValue(fieldValue)) return null;
                      return (
                        <FormItem>
                          <FormLabel>{t("form.behavior")}</FormLabel>
                          <FormControl>
                            {isReadOnly ? (
                              <ReadOnlyField value={fieldValue} />
                            ) : (
                              <AutoGrowTextarea
                                placeholder={t("form.behaviorPlaceholder")}
                                {...field}
                              />
                            )}
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      );
                    }}
                  />

                  <FormField
                    control={form.control}
                    name="diet"
                    render={({ field }) => {
                      const fieldValue = trimTextValue(field.value);
                      if (isReadOnly && !hasValue(fieldValue)) return null;
                      return (
                        <FormItem>
                          <FormLabel>{t("form.diet")}</FormLabel>
                          <FormControl>
                            {isReadOnly ? (
                              <ReadOnlyField value={fieldValue} />
                            ) : (
                              <AutoGrowTextarea placeholder={t("form.dietPlaceholder")} {...field} />
                            )}
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      );
                    }}
                  />

                  <FormField
                    control={form.control}
                    name="environment"
                    render={({ field }) => {
                      const fieldValue = trimTextValue(field.value);
                      if (isReadOnly && !hasValue(fieldValue)) return null;
                      return (
                        <FormItem>
                          <FormLabel>{t("form.environment")}</FormLabel>
                          <FormControl>
                            {isReadOnly ? (
                              <ReadOnlyField value={fieldValue} />
                            ) : (
                              <AutoGrowTextarea
                                placeholder={t("form.environmentPlaceholder")}
                                {...field}
                              />
                            )}
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      );
                    }}
                  />
                </>
              )}
                  </>
                ),
              },
              {
                value: "exame-fisico",
                label: t("form.tabs.physicalExam"),
                content: (
                  <>
              {isReadOnly && !hasExameFisicoData(form.getValues()) ? (
                <div className="flex items-center justify-center py-12 text-center">
                  <p className="text-sm text-muted-foreground">
                    {t("form.emptySection")}
                  </p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="weight"
                      render={({ field }) => {
                        const fieldValue = appendSuffix(formatNumberValue(field.value), "kg");
                        if (isReadOnly && !hasValue(fieldValue)) return null;
                        return (
                          <FormItem>
                            <FormLabel>{t("form.weight")}</FormLabel>
                            <FormControl>
                              {isReadOnly ? (
                                <ReadOnlyField value={fieldValue} />
                              ) : (
                                <Input
                                  type="number"
                                  max={10000}
                                  step="0.01"
                                  placeholder="0.00"
                                  {...field}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    if (!value) {
                                      field.onChange(undefined);
                                      return;
                                    }
                                    const parsed = parseFloat(value);
                                    field.onChange(
                                      Number.isNaN(parsed) ? undefined : Math.min(parsed, 10000),
                                    );
                                  }}
                                  value={field.value || ""}
                                />
                              )}
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        );
                      }}
                    />

                    <FormField
                      control={form.control}
                      name="temperature"
                      render={({ field }) => {
                        const fieldValue = appendSuffix(formatNumberValue(field.value), "°C");
                        if (isReadOnly && !hasValue(fieldValue)) return null;
                        return (
                          <FormItem>
                            <FormLabel>{t("form.temperature")}</FormLabel>
                            <FormControl>
                              {isReadOnly ? (
                                <ReadOnlyField value={fieldValue} />
                              ) : (
                                <Input
                                  type="number"
                                  step="0.1"
                                  placeholder="0.0"
                                  {...field}
                                  onChange={(e) =>
                                    field.onChange(
                                      e.target.value ? parseFloat(e.target.value) : undefined
                                    )
                                  }
                                  value={field.value || ""}
                                />
                              )}
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        );
                      }}
                    />

                    <FormField
                      control={form.control}
                      name="heart_rate"
                      render={({ field }) => {
                        const fieldValue = appendSuffix(formatNumberValue(field.value), "bpm");
                        if (isReadOnly && !hasValue(fieldValue)) return null;
                        return (
                          <FormItem>
                            <FormLabel>{t("form.heartRate")}</FormLabel>
                            <FormControl>
                              {isReadOnly ? (
                                <ReadOnlyField value={fieldValue} />
                              ) : (
                                <Input
                                  type="number"
                                  placeholder="0"
                                  {...field}
                                  onChange={(e) =>
                                    field.onChange(
                                      e.target.value ? parseInt(e.target.value) : undefined
                                    )
                                  }
                                  value={field.value || ""}
                                />
                              )}
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        );
                      }}
                    />

                    <FormField
                      control={form.control}
                      name="respiratory_rate"
                      render={({ field }) => {
                        const fieldValue = appendSuffix(formatNumberValue(field.value), "rpm");
                        if (isReadOnly && !hasValue(fieldValue)) return null;
                        return (
                          <FormItem>
                            <FormLabel>{t("form.respiratoryRate")}</FormLabel>
                            <FormControl>
                              {isReadOnly ? (
                                <ReadOnlyField value={fieldValue} />
                              ) : (
                                <Input
                                  type="number"
                                  placeholder="0"
                                  {...field}
                                  onChange={(e) =>
                                    field.onChange(
                                      e.target.value ? parseInt(e.target.value) : undefined
                                    )
                                  }
                                  value={field.value || ""}
                                />
                              )}
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        );
                      }}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="mucous_membranes"
                    render={({ field }) => {
                      const fieldValue = trimTextValue(field.value);
                      if (isReadOnly && !hasValue(fieldValue)) return null;
                      return (
                        <FormItem>
                          <FormLabel>{t("form.mucousMembranes")}</FormLabel>
                          <FormControl>
                            {isReadOnly ? (
                              <ReadOnlyField value={fieldValue} />
                            ) : (
                              <Input placeholder={t("form.mucousMembranesPlaceholder")} {...field} />
                            )}
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      );
                    }}
                  />

                  <FormField
                    control={form.control}
                    name="capillary_refill_time"
                    render={({ field }) => {
                      const fieldValue = trimTextValue(field.value);
                      if (isReadOnly && !hasValue(fieldValue)) return null;
                      return (
                        <FormItem>
                          <FormLabel>{t("form.crt")}</FormLabel>
                          <FormControl>
                            {isReadOnly ? (
                              <ReadOnlyField value={fieldValue} />
                            ) : (
                              <Input placeholder={t("form.crtPlaceholder")} {...field} />
                            )}
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      );
                    }}
                  />

                  <FormField
                    control={form.control}
                    name="hydration_status"
                    render={({ field }) => {
                      const fieldValue = trimTextValue(field.value);
                      if (isReadOnly && !hasValue(fieldValue)) return null;
                      return (
                        <FormItem>
                          <FormLabel>{t("form.hydrationStatus")}</FormLabel>
                          <FormControl>
                            {isReadOnly ? (
                              <ReadOnlyField value={fieldValue} />
                            ) : (
                              <Input placeholder={t("form.hydrationStatusPlaceholder")} {...field} />
                            )}
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      );
                    }}
                  />

                  <FormField
                    control={form.control}
                    name="lymph_nodes"
                    render={({ field }) => {
                      const fieldValue = trimTextValue(field.value);
                      if (isReadOnly && !hasValue(fieldValue)) return null;
                      return (
                        <FormItem>
                          <FormLabel>{t("form.lymphNodes")}</FormLabel>
                          <FormControl>
                            {isReadOnly ? (
                              <ReadOnlyField value={fieldValue} />
                            ) : (
                              <AutoGrowTextarea
                                placeholder={t("form.lymphNodesPlaceholder")}
                                {...field}
                              />
                            )}
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      );
                    }}
                  />

                  <FormField
                    control={form.control}
                    name="cardiac_auscultation"
                    render={({ field }) => {
                      const fieldValue = trimTextValue(field.value);
                      if (isReadOnly && !hasValue(fieldValue)) return null;
                      return (
                        <FormItem>
                          <FormLabel>{t("form.cardiacAuscultation")}</FormLabel>
                          <FormControl>
                            {isReadOnly ? (
                              <ReadOnlyField value={fieldValue} />
                            ) : (
                              <AutoGrowTextarea
                                placeholder={t("form.cardiacAuscultationPlaceholder")}
                                {...field}
                              />
                            )}
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      );
                    }}
                  />

                  <FormField
                    control={form.control}
                    name="respiratory_auscultation"
                    render={({ field }) => {
                      const fieldValue = trimTextValue(field.value);
                      if (isReadOnly && !hasValue(fieldValue)) return null;
                      return (
                        <FormItem>
                          <FormLabel>{t("form.respiratoryAuscultation")}</FormLabel>
                          <FormControl>
                            {isReadOnly ? (
                              <ReadOnlyField value={fieldValue} />
                            ) : (
                              <AutoGrowTextarea
                                placeholder={t("form.respiratoryAuscultationPlaceholder")}
                                {...field}
                              />
                            )}
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      );
                    }}
                  />

                  <FormField
                    control={form.control}
                    name="abdominal_palpation"
                    render={({ field }) => {
                      const fieldValue = trimTextValue(field.value);
                      if (isReadOnly && !hasValue(fieldValue)) return null;
                      return (
                        <FormItem>
                          <FormLabel>{t("form.abdominalPalpation")}</FormLabel>
                          <FormControl>
                            {isReadOnly ? (
                              <ReadOnlyField value={fieldValue} />
                            ) : (
                              <AutoGrowTextarea
                                placeholder={t("form.abdominalPalpationPlaceholder")}
                                {...field}
                              />
                            )}
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      );
                    }}
                  />

                  <FormField
                    control={form.control}
                    name="locomotor_system"
                    render={({ field }) => {
                      const fieldValue = trimTextValue(field.value);
                      if (isReadOnly && !hasValue(fieldValue)) return null;
                      return (
                        <FormItem>
                          <FormLabel>{t("form.locomotorSystem")}</FormLabel>
                          <FormControl>
                            {isReadOnly ? (
                              <ReadOnlyField value={fieldValue} />
                            ) : (
                              <AutoGrowTextarea
                                placeholder={t("form.locomotorSystemPlaceholder")}
                                {...field}
                              />
                            )}
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      );
                    }}
                  />

                  <FormField
                    control={form.control}
                    name="skin_coat"
                    render={({ field }) => {
                      const fieldValue = trimTextValue(field.value);
                      if (isReadOnly && !hasValue(fieldValue)) return null;
                      return (
                        <FormItem>
                          <FormLabel>{t("form.skinCoat")}</FormLabel>
                          <FormControl>
                            {isReadOnly ? (
                              <ReadOnlyField value={fieldValue} />
                            ) : (
                              <AutoGrowTextarea
                                placeholder={t("form.skinCoatPlaceholder")}
                                {...field}
                              />
                            )}
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      );
                    }}
                  />

                  <FormField
                    control={form.control}
                    name="other_systems"
                    render={({ field }) => {
                      const fieldValue = trimTextValue(field.value);
                      if (isReadOnly && !hasValue(fieldValue)) return null;
                      return (
                        <FormItem>
                          <FormLabel>{t("form.otherSystems")}</FormLabel>
                          <FormControl>
                            {isReadOnly ? (
                              <ReadOnlyField value={fieldValue} />
                            ) : (
                              <AutoGrowTextarea
                                placeholder={t("form.otherSystemsPlaceholder")}
                                {...field}
                              />
                            )}
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      );
                    }}
                  />
                </>
              )}
                  </>
                ),
              },
              {
                value: "diagnostico",
                label: t("form.tabs.diagnosis"),
                content: (
                  <>
              {isReadOnly && !hasDiagnosticoData(form.getValues()) ? (
                <div className="flex items-center justify-center py-12 text-center">
                  <p className="text-sm text-muted-foreground">
                    {t("form.emptySection")}
                  </p>
                </div>
              ) : (
                <>
                  <FormField
                    control={form.control}
                    name="presumptive_diagnosis"
                    render={({ field }) => {
                      const fieldValue = trimTextValue(field.value);
                      if (isReadOnly && !hasValue(fieldValue)) return null;
                      return (
                        <FormItem>
                          <FormLabel>{t("form.differentialDiagnosis")}</FormLabel>
                          <FormControl>
                            {isReadOnly ? (
                              <ReadOnlyField value={fieldValue} />
                            ) : (
                              <AutoGrowTextarea
                                placeholder={t("form.differentialDiagnosisPlaceholder")}
                                {...field}
                              />
                            )}
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      );
                    }}
                  />

                  <FormField
                    control={form.control}
                    name="definitive_diagnosis"
                    render={({ field }) => {
                      const fieldValue = trimTextValue(field.value);
                      if (isReadOnly && !hasValue(fieldValue)) return null;
                      return (
                        <FormItem>
                          <FormLabel>{t("form.definitiveDiagnosis")}</FormLabel>
                          <FormControl>
                            {isReadOnly ? (
                              <ReadOnlyField value={fieldValue} />
                            ) : (
                              <AutoGrowTextarea
                                placeholder={t("form.definitiveDiagnosisPlaceholder")}
                                {...field}
                              />
                            )}
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      );
                    }}
                  />

                  <div className="rounded-lg border border-border/60 bg-muted/20 p-4 space-y-4">
                    <FormField
                      control={form.control}
                      name="register_death"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                          <FormControl>
                            <Checkbox
                              checked={field.value ?? false}
                              onCheckedChange={field.onChange}
                              disabled={isReadOnly}
                            />
                          </FormControl>
                          <div className="space-y-0.5 leading-none">
                            <FormLabel>{t("form.registerDeath")}</FormLabel>
                          </div>
                        </FormItem>
                      )}
                    />

                    {form.watch("register_death") ? (
                      <div className="grid gap-4">
                        <FormField
                          control={form.control}
                          name="death_date"
                          render={({ field }) => (
                            <FormItem className="flex flex-col">
                              <FormLabel>{t("form.deathDate")}</FormLabel>
                              <FormControl>
                                {isReadOnly ? (
                                  <ReadOnlyField value={formatDateTimeForReadOnly(field.value)} />
                                ) : (
                                  <DateFieldPopover
                                    trigger={
                                      <Button variant="outline" className="w-full justify-between">
                                        {field.value ? format(field.value, "dd/MM/yyyy") : t("form.selectDeathDate")}
                                        <CalendarIcon className="h-4 w-4 opacity-50" />
                                      </Button>
                                    }
                                    selected={field.value}
                                    onSelect={field.onChange}
                                  />
                                )}
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="death_cause"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>{t("form.deathCause")}</FormLabel>
                              <FormControl>
                                {isReadOnly ? (
                                  <ReadOnlyField value={trimTextValue(field.value)} />
                                ) : (
                                  <AutoGrowTextarea placeholder={t("form.deathCausePlaceholder")} {...field} />
                                )}
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    ) : null}
                  </div>
                </>
              )}
                  </>
                ),
              },
              {
                value: "tratamento",
                label: t("form.tabs.treatment"),
                content: (
                  <>
              {isReadOnly && !hasTratamentoData(form.getValues()) ? (
                <div className="flex items-center justify-center py-12 text-center">
                  <p className="text-sm text-muted-foreground">
                    {t("form.emptySection")}
                  </p>
                </div>
              ) : (
                <>
                  <FormField
                    control={form.control}
                    name="treatment"
                    render={({ field }) => {
                      const fieldValue = trimTextValue(field.value);
                      if (isReadOnly && !hasValue(fieldValue)) return null;
                      return (
                        <FormItem>
                          <FormLabel>{t("form.treatmentConduct")}</FormLabel>
                          <FormControl>
                            {isReadOnly ? (
                              <ReadOnlyField value={fieldValue} />
                            ) : (
                              <AutoGrowTextarea
                                placeholder={t("form.treatmentConductPlaceholder")}
                                {...field}
                              />
                            )}
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      );
                    }}
                  />

                  <FormField
                    control={form.control}
                    name="follow_up_date"
                    render={({ field }) => {
                      const fieldValue = formatDateForReadOnly(field.value);
                      if (isReadOnly && !hasValue(fieldValue)) return null;
                      return (
                        <FormItem className="flex flex-col">
                          <FormLabel>{t("form.followUpDate")}</FormLabel>
                          <FormControl>
                            {isReadOnly ? (
                              <ReadOnlyField
                                value={fieldValue}
                                fallback={t("form.followUpNotScheduled")}
                              />
                            ) : (
                              <Popover>
                                <PopoverTrigger asChild>
                                  <Button
                                    variant="outline"
                                    className={cn(
                                      "w-full pl-3 text-left font-normal",
                                      !field.value && "text-muted-foreground"
                                    )}
                                  >
                                    {field.value ? (
                                      format(field.value instanceof Date ? field.value : new Date(field.value), "dd/MM/yyyy")
                                    ) : (
                                      <span>{t("form.selectDate")}</span>
                                    )}
                                    <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0" align="start">
                                  <div className="p-3">
                                    <Calendar
                                      mode="single"
                                      selected={field.value ? new Date(field.value) : undefined}
                                      onSelect={(date) => {
                                        if (!date) {
                                          field.onChange(undefined);
                                          return;
                                        }

                                        field.onChange(new Date(
                                          date.getFullYear(),
                                          date.getMonth(),
                                          date.getDate(),
                                          12,
                                          0,
                                          0,
                                          0,
                                        ));
                                      }}
                                      disabled={(date) => {
                                        const today = new Date();
                                        today.setHours(0, 0, 0, 0);
                                        return date < today;
                                      }}
                                      initialFocus
                                    />
                                    <Button
                                      variant="ghost"
                                      type="button"
                                      onClick={() => {
                                        field.onChange(undefined);
                                        form.clearErrors(["create_follow_up_appointment", "follow_up_time"]);
                                        form.setValue("create_follow_up_appointment", false, { shouldDirty: true, shouldTouch: true });
                                        form.setValue("follow_up_time", "", { shouldDirty: true, shouldTouch: true });
                                      }}
                                      className="mt-2 justify-start px-0 text-destructive hover:text-destructive focus-visible:text-destructive"
                                    >
                                      {t("form.removeDate")}
                                    </Button>
                                  </div>
                                </PopoverContent>
                              </Popover>
                            )}
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      );
                    }}
                  />

                  {!isReadOnly && followUpDate ? (
                    <div className="rounded-md border border-border/60">
                      <div className="flex items-center justify-between gap-4 px-3 py-2">
                        <div className="space-y-1">
                          <p className="text-sm font-medium">{t("form.createAppointment")}</p>
                          <p className="text-xs text-muted-foreground">{t("form.createAppointmentDescription")}</p>
                        </div>
                        <Switch
                          checked={Boolean(createFollowUpAppointment)}
                          onCheckedChange={(checked) => {
                            const enabled = Boolean(checked);
                            form.setValue("create_follow_up_appointment", enabled, { shouldDirty: true, shouldTouch: true });
                            if (!enabled) {
                              form.clearErrors("follow_up_time");
                              form.setValue("follow_up_time", "", { shouldDirty: true, shouldTouch: true });
                            }
                          }}
                        />
                      </div>

                      {createFollowUpAppointment ? (
                        <div className="border-t border-border/60 px-3 py-3">
                          <FormField
                            control={form.control}
                            name="follow_up_time"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>{t("form.appointmentTime")}</FormLabel>
                                <FormControl>
                                  <Input
                                    type="time"
                                    {...field}
                                    value={field.value || ""}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      ) : null}
                    </div>
                  ) : null}

                  {!isReadOnly && errors.create_follow_up_appointment?.message ? (
                    <p className="text-sm text-destructive">{errors.create_follow_up_appointment.message}</p>
                  ) : null}

                  <FormField
                    control={form.control}
                    name="follow_up_notes"
                    render={({ field }) => {
                      const fieldValue = trimTextValue(field.value);
                      if (isReadOnly && !hasValue(fieldValue)) return null;
                      return (
                        <FormItem>
                          <FormLabel>{t("form.followUpNotes")}</FormLabel>
                          <FormControl>
                            {isReadOnly ? (
                              <ReadOnlyField value={fieldValue} />
                            ) : (
                              <AutoGrowTextarea
                                placeholder={t("form.followUpNotesPlaceholder")}
                                {...field}
                              />
                            )}
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      );
                    }}
                  />

                </>
              )}
                  </>
                ),
              },
            ]}
          />
        </form>
      </Form>
    );
  });

MedicalRecordForm.displayName = "MedicalRecordForm";
